import { Link } from 'react-router-dom';
import { useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

export default function ServicesPage() {
  // Scroll reveal observer
  useEffect(() => {
    const elements = Array.from(
      document.querySelectorAll('.reveal, .reveal-left, .reveal-right, .reveal-scale, .stagger-children')
    );

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.05, rootMargin: '0px 0px -20px 0px' }
    );

    elements.forEach((el) => observer.observe(el));

    // Filet de sécurité : révèle tout élément déjà visible à l'écran
    // (coupe court aux cas limites de l'observeur sur les grandes grilles)
    const fallback = setTimeout(() => {
      elements.forEach((el) => {
        const rect = el.getBoundingClientRect();
        if (rect.top < window.innerHeight && rect.bottom > 0) {
          el.classList.add('visible');
        }
      });
    }, 600);

    return () => {
      observer.disconnect();
      clearTimeout(fallback);
    };
  }, []);

  const specialties = [
    { 
      name: 'Médecine Générale', 
      icon: 'ri-stethoscope-line',
      description: 'Consultations générales, diagnostics et soins de santé primaires pour toute la famille. Nos médecins généralistes sont disponibles pour vous accompagner dans tous vos besoins de santé quotidiens.',
      services: ['Consultation générale', 'Bilan de santé', 'Suivi médical', 'Certificats médicaux'],
      image: 'https://ddfascfiaucteirbzkzd.supabase.co/storage/v1/object/public/assets/services/medecine-generale.webp'
    },
    { 
      name: 'Urologie et Chirurgie Générale', 
      icon: 'ri-surgical-mask-line',
      description: 'Interventions chirurgicales et traitements urologiques avec des équipements modernes et une équipe expérimentée.',
      services: ['Chirurgie ambulatoire', 'Chirurgie digestive', 'Urologie', 'Chirurgie d\'urgence'],
      image: 'https://ddfascfiaucteirbzkzd.supabase.co/storage/v1/object/public/assets/services/chirurgie.webp'
    },
    { 
      name: 'Maternité (Sage-femme)', 
      icon: 'ri-parent-line',
      description: 'Accompagnement complet de la grossesse et de l\'accouchement par nos sages-femmes expérimentées dans un environnement sécurisé, chaleureux et respectueux.',
      services: ['Suivi de grossesse', 'Accouchement', 'Consultations prénatales', 'Suivi post-natal', 'Conseils en allaitement'],
      image: 'https://ddfascfiaucteirbzkzd.supabase.co/storage/v1/object/public/assets/services/maternite.webp'
    },
    { 
      name: 'Gynécologie', 
      icon: 'ri-women-line',
      description: 'Consultations gynécologiques, dépistages et prise en charge des pathologies gynécologiques par nos spécialistes dans un cadre confidentiel et moderne.',
      services: ['Consultations gynécologiques', 'Dépistage (frottis, IST)', 'Suivi contraceptif', 'Échographie gynécologique', 'Prise en charge des pathologies'],
      image: 'https://ddfascfiaucteirbzkzd.supabase.co/storage/v1/object/public/assets/services/gynecologie.webp'
    },
    { 
      name: 'Psychologie et Psychiatrie', 
      icon: 'ri-mental-health-line',
      description: 'Prise en charge de votre santé mentale avec des professionnels qualifiés dans un cadre confidentiel et bienveillant.',
      services: ['Consultations psychologiques', 'Suivi psychiatrique', 'Thérapies', 'Gestion du stress'],
      image: 'https://ddfascfiaucteirbzkzd.supabase.co/storage/v1/object/public/assets/services/psychologie.webp'
    },
    { 
      name: 'Pédiatrie', 
      icon: 'ri-bear-smile-line',
      description: 'Soins spécialisés pour les enfants de la naissance à l\'adolescence avec des pédiatres attentionnés.',
      services: ['Consultations pédiatriques', 'Vaccination', 'Suivi de croissance', 'Urgences pédiatriques'],
      image: 'https://ddfascfiaucteirbzkzd.supabase.co/storage/v1/object/public/assets/services/pediatrie.webp'
    },
    { 
      name: 'ORL', 
      icon: 'ri-volume-up-line',
      description: 'Diagnostic et traitement des pathologies des oreilles, du nez et de la gorge par des spécialistes expérimentés.',
      services: ['Consultations ORL', 'Audiométrie', 'Endoscopie', 'Petite chirurgie ORL'],
      image: 'https://ddfascfiaucteirbzkzd.supabase.co/storage/v1/object/public/assets/services/orl.webp'
    },
    { 
      name: 'Ophtalmologie', 
      icon: 'ri-eye-line',
      description: 'Soins oculaires complets avec des équipements de diagnostic de pointe pour préserver votre vision.',
      services: ['Examens de la vue', 'Traitement des pathologies oculaires', 'Chirurgie oculaire', 'Prescription de lunettes'],
      image: 'https://ddfascfiaucteirbzkzd.supabase.co/storage/v1/object/public/assets/services/ophtalmologie.webp'
    },
    { 
      name: 'Kinésithérapie', 
      icon: 'ri-walk-line',
      description: 'Rééducation fonctionnelle et traitement des douleurs musculaires et articulaires.',
      services: ['Rééducation motrice', 'Massage thérapeutique', 'Physiothérapie', 'Réadaptation'],
      image: 'https://ddfascfiaucteirbzkzd.supabase.co/storage/v1/object/public/assets/services/kinesitherapie.webp'
    },
    { 
      name: 'Traumatologie et Orthopédie', 
      icon: 'ri-first-aid-kit-line',
      description: 'Prise en charge des fractures, entorses et pathologies osseuses avec expertise et équipements modernes.',
      services: ['Traitement des fractures', 'Chirurgie orthopédique', 'Pose de plâtre', 'Suivi post-opératoire'],
      image: 'https://ddfascfiaucteirbzkzd.supabase.co/storage/v1/object/public/assets/services/traumatologie.webp'
    },
    { 
      name: 'Hématologie', 
      icon: 'ri-drop-line',
      description: 'Diagnostic et traitement des maladies du sang par des hématologues spécialisés.',
      services: ['Analyses sanguines', 'Traitement des anémies', 'Suivi hématologique', 'Transfusions'],
      image: 'https://ddfascfiaucteirbzkzd.supabase.co/storage/v1/object/public/assets/services/hematologie.webp'
    },
    { 
      name: 'Dermatologie', 
      icon: 'ri-hand-sanitizer-line',
      description: 'Soins de la peau, traitement des pathologies cutanées et consultations esthétiques.',
      services: ['Consultations dermatologiques', 'Traitement de l\'acné', 'Dermatologie esthétique', 'Biopsies cutanées'],
      image: 'https://ddfascfiaucteirbzkzd.supabase.co/storage/v1/object/public/assets/services/dermatologie.webp'
    },
    { 
      name: 'Hépato Gastro Entérologie', 
      icon: 'ri-capsule-line',
      description: 'Diagnostic et traitement des troubles digestifs et hépatiques avec des équipements d\'endoscopie modernes.',
      services: ['Endoscopie digestive', 'Traitement des pathologies digestives', 'Hépatologie'],
      image: 'https://ddfascfiaucteirbzkzd.supabase.co/storage/v1/object/public/assets/services/gastro.webp'
    },
    { 
      name: 'Cardiologie', 
      icon: 'ri-heart-pulse-line',
      description: 'Prévention, diagnostic et traitement des maladies cardiovasculaires avec des cardiologues experts.',
      services: ['Électrocardiogramme', 'Échographie cardiaque', 'Holter', 'MAPA', 'Suivi cardiologique'],
      image: 'https://ddfascfiaucteirbzkzd.supabase.co/storage/v1/object/public/assets/services/cardiologie.webp'
    },
    { 
      name: 'Radiologie et Imagerie médicale', 
      icon: 'ri-scan-line',
      description: 'Imagerie médicale complète pour des diagnostics précis et rapides.',
      services: ['Échographie', 'Radiographie'],
      image: 'https://ddfascfiaucteirbzkzd.supabase.co/storage/v1/object/public/assets/services/radiologie.webp'
    },
    { 
      name: 'Médecine physique et Réadaptation', 
      icon: 'ri-wheelchair-line',
      description: 'Programmes de réadaptation personnalisés pour retrouver autonomie et mobilité.',
      services: ['Rééducation fonctionnelle', 'Ergothérapie', 'Réadaptation', 'Suivi post-traumatique'],
      image: 'https://ddfascfiaucteirbzkzd.supabase.co/storage/v1/object/public/assets/services/reeducation.webp'
    },
    { 
      name: 'Pneumologie', 
      icon: 'ri-lungs-line',
      description: 'Diagnostic et traitement des maladies respiratoires par des pneumologues qualifiés.',
      services: ['Spirométrie', 'Traitement de l\'asthme', 'Suivi des pathologies respiratoires', 'Oxygénothérapie'],
      image: 'https://ddfascfiaucteirbzkzd.supabase.co/storage/v1/object/public/assets/services/pneumologie.webp'
    },
    { 
      name: 'Endocrinologie', 
      icon: 'ri-test-tube-line',
      description: 'Prise en charge des troubles hormonaux et métaboliques comme le diabète.',
      services: ['Traitement du diabète', 'Troubles thyroïdiens', 'Bilan hormonal', 'Suivi endocrinologique'],
      image: 'https://ddfascfiaucteirbzkzd.supabase.co/storage/v1/object/public/assets/services/endocrinologie.webp'
    },
    { 
      name: 'Neurologie et neurochirurgie', 
      icon: 'ri-brain-line',
      description: 'Diagnostic et traitement des pathologies du système nerveux central et périphérique.',
      services: ['Consultations neurologiques', 'Neurochirurgie', 'Suivi neurologique'],
      image: 'https://ddfascfiaucteirbzkzd.supabase.co/storage/v1/object/public/assets/services/neurologie.webp'
    },
    { 
      name: 'Vaccination', 
      icon: 'ri-syringe-line',
      description: 'Programme complet de vaccination pour tous les âges selon le calendrier vaccinal.',
      services: ['Vaccination PEV - Pédiatriques', 'Rappels vaccinaux'],
      image: 'https://ddfascfiaucteirbzkzd.supabase.co/storage/v1/object/public/assets/services/vaccination.webp'
    },
    { 
      name: 'Laboratoire d\'analyses biomédicales', 
      icon: 'ri-microscope-line',
      description: 'Analyses médicales complètes et fiables avec résultats rapides.',
      services: ['Analyses sanguines', 'Analyses d\'urine', 'Bactériologie - Virologie', 'Parasitologie', 'Sérologie'],
      image: 'https://ddfascfiaucteirbzkzd.supabase.co/storage/v1/object/public/assets/services/laboratoire.webp'
    },
  ];

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Header />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-clinic-blue to-clinic-blue-dark text-white py-16 md:py-24 overflow-hidden">
        {/* Floating decorative elements */}
        <div className="absolute top-8 left-8 w-24 h-24 bg-white/5 rounded-full animate-float hidden lg:block"></div>
        <div className="absolute bottom-12 right-12 w-16 h-16 bg-white/5 rounded-full animate-float hidden lg:block" style={{ animationDelay: '2s' }}></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="hero-title text-3xl md:text-5xl font-bold mb-4 md:mb-6">
              Nos Services Médicaux
            </h1>
            <p className="hero-subtitle text-base md:text-xl text-white/90 leading-relaxed">
              Plus de 20 spécialités médicales pour répondre à tous vos besoins de santé avec des équipements modernes et une équipe dévouée
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-8 sm:py-12 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 md:gap-8 stagger-children">
            {specialties.map((specialty, index) => (
              <div 
                key={index}
                className="bg-white p-4 sm:p-6 md:p-8 rounded-3xl border border-gray-100 hover:border-clinic-green/30 transition-all duration-500 group card-elevated"
              >
                {specialty.image && (
                  <div className="w-full h-48 sm:h-56 md:h-64 mb-4 sm:mb-6 rounded-2xl overflow-hidden">
                    <img 
                      src={specialty.image} 
                      alt={specialty.name}
                      className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                )}
                <div className="flex items-start gap-3 sm:gap-4 md:gap-6">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base sm:text-lg md:text-2xl font-bold text-clinic-blue-dark mb-2 md:mb-3 group-hover:text-clinic-green transition-colors duration-500">
                      {specialty.name}
                    </h3>
                    <p className="text-sm md:text-base text-gray-600 mb-3 md:mb-4 leading-relaxed">
                      {specialty.description}
                    </p>
                    <div className="space-y-2">
                      <p className="text-xs md:text-sm font-semibold text-clinic-blue-dark">Services proposés :</p>
                      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {specialty.services.map((service, idx) => (
                          <li key={idx} className="flex items-start text-xs md:text-sm text-gray-600">
                            <i className="ri-check-line text-clinic-green mr-2 mt-0.5 flex-shrink-0"></i>
                            <span>{service}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact CTA Section */}
      <section className="py-8 sm:py-12 md:py-20 bg-gradient-to-b from-white to-clinic-blue/5">
        <div className="max-w-4xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8">
          <div className="bg-white p-5 sm:p-8 md:p-12 rounded-3xl border border-gray-100 shadow-[var(--shadow-elevated)] reveal-scale">
            <div className="text-center mb-8 reveal">
              <h2 className="text-2xl md:text-4xl font-bold text-clinic-blue-dark mb-4">
                Besoin de plus d'informations ?
              </h2>
              <p className="text-base md:text-lg text-gray-600">
                Notre équipe est à votre disposition pour répondre à toutes vos questions
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8 stagger-children">
              <div className="flex items-start gap-4 p-4 bg-clinic-blue/5 rounded-2xl card-lift">
                <div className="w-12 h-12 bg-clinic-green/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-phone-line text-2xl text-clinic-green"></i>
                </div>
                <div>
                  <h3 className="font-semibold text-clinic-blue-dark mb-2">Téléphone</h3>
                  <p className="text-sm text-gray-600">+229 01 95 40 58 91</p>
                  <p className="text-sm text-gray-600">+229 01 94 27 54 24</p>
                </div>
              </div>

              <div className="flex items-start gap-4 p-4 bg-clinic-blue/5 rounded-2xl card-lift">
                <div className="w-12 h-12 bg-clinic-green/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-mail-line text-2xl text-clinic-green"></i>
                </div>
                <div>
                  <h3 className="font-semibold text-clinic-blue-dark mb-2">Email</h3>
                  <p className="text-sm text-gray-600">clinicfam@yahoo.com</p>
                </div>
              </div>

              <div className="flex items-start gap-4 p-4 bg-clinic-blue/5 rounded-2xl md:col-span-2 card-lift">
                <div className="w-12 h-12 bg-clinic-green/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-map-pin-line text-2xl text-clinic-green"></i>
                </div>
                <div>
                  <h3 className="font-semibold text-clinic-blue-dark mb-2">Adresse</h3>
                  <p className="text-sm text-gray-600">04 BP 127 lot 1862 Fifadji Cotonou, Bénin</p>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center reveal">
              <Link
                to="/contact"
                className="btn-ripple btn-accent px-8 py-3 rounded-full font-semibold text-base text-center whitespace-nowrap"
              >
                Nous contacter
              </Link>
              <Link
                to="/rendez-vous"
                className="btn-ripple btn-primary px-8 py-3 rounded-full font-semibold text-base text-center whitespace-nowrap"
              >
                Prendre rendez-vous
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}